import { Injectable } from '@angular/core';
import { Observable, of, ReplaySubject, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AuthUtils } from './auth.utils';
import { Roles } from '../Enums/Roles';
import { UserService } from '../user/user.service';
import { NavigationService } from '../navigation/navigation.service';
import { AccountClient, Login, MainRoleChoose, TokenModel } from 'Nswag/nswag-api-auth';

@Injectable()
export class AuthService
{
    private _authenticated: boolean = false;
    private _authenticated$: ReplaySubject<boolean> = new ReplaySubject(1);

    constructor(
        private accountClient: AccountClient,
        private userService: UserService,
        private navigationService: NavigationService
    )
    {
        
        this._authenticated$.next(!AuthUtils.isTokenExpired(this.accessToken));
        this._authenticated = !AuthUtils.isTokenExpired(this.accessToken);

    }

    set accessToken(token: string)
    {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string
    {
        return localStorage.getItem('accessToken') ?? '';
    }

    // get isAuthenticated(): boolean {
    //     return this._authenticated;
    // }

    get authenticated$(): Observable<boolean>
    {
        return this._authenticated$.asObservable();
    }

    signIn(credentials: Login): Observable<TokenModel>
    {
        // Throw error, if the user is already logged in
        if (this._authenticated)
        {
            return throwError(() => new Error('User is already logged in.'));
        }

        return this.accountClient.login(credentials).pipe(
            map(tokenModel =>
            {
                // Store the access token in the local storage
                this.accessToken = tokenModel.token;
                this.navigationService.updateNavigationByUserPermissions();

                // Set the authenticated flag to true
                this._authenticated = true;
                this._authenticated$.next(true);

                // Return a new observable with the response
                return tokenModel;
            })
        );
    }

    /**
     * Sign out
     */
    signOut(): Observable<boolean>
    {
        localStorage.removeItem('accessToken');
        // this.userService.user = null;

        // Set the authenticated flag to false
        this._authenticated = false;
        this._authenticated$.next(false);

        // Return the observable
        return of(true);
    }

    changeMainRole(mainRole: Roles): Observable<TokenModel>
    {
        return this.accountClient
            .chooseMainRole(
                new MainRoleChoose({
                    mainRole: mainRole,
                    userName: this.userService.user.userName,
                })
            )
            .pipe(
                map(tokenModel =>
                {
                    this.accessToken = tokenModel.token;
                    // this.userService.get();

                    return tokenModel;
                })
            );
    }

    /**
     * Sign in using the access token
     */
    private signInUsingToken(): Observable<boolean>
    {
        // Renew token
        return this.accountClient.refreshAccessToken().pipe(
            catchError(() =>
                // Return false
                of(false)
            ),
            switchMap((response: TokenModel) =>
            {
                this.navigationService.updateNavigationByUserPermissions();

                // Store the access token in the local storage
                this.accessToken = response.token;

                // Set the authenticated flag to true
                this._authenticated = true;
                this._authenticated$.next(true);

                // Store the user on the user service
                // this.userService.user = response.user;

                // Return true
                return of(true);
            })
        );
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean>
    {
        // Check if the user is logged in
        if (this._authenticated)
        {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken)
        {
            return of(false);
        }

        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken))
        {
            return of(false);
        }

        // If the access token exists and it didn't expire, sign in using it
        return this.signInUsingToken();
    }

    // chooseMainPosition(mainPositionId: string): Observable<any> {
    //     return this.http
    //         .post<any>(
    //             `${this.baseUrl}/chooseMainPosition/${mainPositionId}`,
    //             null
    //         )
    //         .pipe(
    //             map((user) => {
    //                 // store user details and jwt token in local storage to keep user logged in between page refreshes
    //                 localStorage.setItem('currentUser', JSON.stringify(user));
    //                 this.currentUserSubject.next(user);
    //                 this.getUserProfile();
    //                 return user;
    //             })
    //         );
    // }

    // getLang(): string {
    //     const i = localStorage.getItem(this.langKey);
    //     if (i) {
    //         return i as string;
    //     } else {
    //         return this.defaultLang;
    //     }
    // }

    // setLang(lang: string): void {
    //     if (lang) {
    //         localStorage.setItem(this.langKey, lang);
    //     } else {
    //         localStorage.removeItem(this.langKey);
    //     }
    // }
}
